// #tag: CONTENT MANAGEMENT SYSTEM (CMS) API CLIENT
// #purpose: Dispatches API queries for dynamic page sections, global site configs, exam taxonomies, timeline milestones, and dashboard widgets.
import { apiGet } from './client';

export interface PageSection {
  id: string;
  page: string;
  sectionType: string;
  title: string | null;
  subtitle: string | null;
  displayOrder: number;
  isEnabled: boolean;
  configuration: Record<string, unknown>;
}

export interface SiteConfig {
  platform?: {
    name: string;
    tagline: string;
    subtagline: string;
    logoPath: string;
    copyright: string;
  };
  footer?: {
    description: string;
    columns: Array<{ title: string; links: Array<{ label: string; route: string }> }>;
  };
}

export interface ExamTypeDto {
  id: string;
  name: string;
  slug: string;
  icon: string;
  themeColor: string;
  displayOrder: number;
  isActive: boolean;
}

export interface ResultCategoryDto {
  id: string;
  label: string;
  displayOrder: number;
}

export interface AdmitCardStatusDto {
  id: string;
  label: string;
  displayOrder: number;
}

export interface VaultCategoryDto {
  id: string;
  label: string;
  items: string[];
  icon: string;
  displayOrder: number;
}

export interface VaultDocumentDto {
  id: string;
  name: string;
  categoryId: string;
  subcategory: string;
  uploadedAt: string;
  expiryDate?: string;
  expiryStatus?: 'valid' | 'expiring' | 'expired';
  fileSize: string;
  fileName?: string;
  mimeType?: string;
  version: number;
  sequenceOrder?: number;
  tags?: string[];
  description?: string;
  detectedDocType?: string;
  aiAnalysisStatus?: 'pending' | 'complete' | 'failed' | 'skipped';
  aiAnalysisAt?: string;
  aiMetadata?: Record<string, unknown>;
  fileAvailable?: boolean;
}

export interface DashboardWidgetDto {
  id: string;
  widgetType: string;
  title: string | null;
  displayOrder: number;
  visibility: string;
  configuration: Record<string, unknown>;
  isEnabled: boolean;
}

export interface ExamTimelineDto {
  id: string;
  examId: string;
  examTitle: string;
  steps: Array<{ id: string; label: string; status: string; date?: string }>;
}

export interface NavigationItemDto {
  id: string;
  title: string;
  shortTitle: string;
  route: string;
  icon: string;
  displayOrder: number;
  permission: string;
  visible: boolean;
  context: string;
}

export function fetchNavigation(context = 'primary'): Promise<{ data: NavigationItemDto[] }> {
  return apiGet(`/navigation?context=${context}`);
}

export function fetchSiteConfig(): Promise<SiteConfig> {
  return apiGet('/site-config');
}

export function fetchPageSections(page: string): Promise<{ data: PageSection[] }> {
  return apiGet(`/pages/${page}/sections`);
}

export function fetchExamTypes(): Promise<{ data: ExamTypeDto[] }> {
  return apiGet('/exam-types');
}

export function fetchResultCategories(): Promise<{ data: ResultCategoryDto[] }> {
  return apiGet('/result-categories');
}

export function fetchAdmitCardStatuses(): Promise<{ data: AdmitCardStatusDto[] }> {
  return apiGet('/admit-card-statuses');
}

export function fetchVaultCategories(): Promise<{ data: VaultCategoryDto[] }> {
  return apiGet('/vault/categories');
}

export function fetchDashboardWidgets(): Promise<{ data: DashboardWidgetDto[] }> {
  return apiGet('/dashboard/widgets');
}

export function fetchExamTimeline(id: string): Promise<ExamTimelineDto> {
  return apiGet(`/dashboard/timelines/${id}`);
}

export function fetchExamTimelineByExamId(examId: string): Promise<ExamTimelineDto> {
  return apiGet(`/dashboard/timelines/by-exam/${encodeURIComponent(examId)}`);
}

export interface DashboardSummaryDto {
  savedExamsCount: number;
  unreadNotificationsCount: number;
  upcomingEventsCount: number;
  vaultDocumentsCount: number;
}

export function fetchDashboardSummary(): Promise<DashboardSummaryDto> {
  return apiGet('/dashboard/summary');
}
